// ============================================================
// Shared helpers for the custom webshop views (grid/list/single)
// ============================================================

// Product detail page toggle (PIT-TASK-2027-000166). When
// Webshop Settings.enable_product_detail_page is off, product cards
// must not link to the detail page (the page itself redirects to the
// listing, enforced server-side in website_item.py).
// Default ON when the setting is missing (older cached payloads) —
// mirrors the server-side fail-open default of
// is_product_detail_page_enabled().
webshop.detail_page_enabled = function (settings) {
	if (!settings) return true;
	return cint(settings.enable_product_detail_page, 1) !== 0;
};

// Returns the href for an item's detail page, or null when the detail
// page is disabled — callers then render the content WITHOUT an <a>.
webshop.get_item_link = function (item, settings) {
	if (!webshop.detail_page_enabled(settings)) return null;
	return "/" + (item.route || "#");
};

// Single view toggle (PIT-TASK-2027-000166). When
// Webshop Settings.enable_single_view is off, the listing view toggler
// hides the "Single View" button and a stale localStorage preference
// falls back to the grid view.
// Default ON when the setting is missing (older cached payloads).
webshop.single_view_enabled = function (settings) {
	if (!settings) return true;
	return cint(settings.enable_single_view, 1) !== 0;
};

// Aspect-ratio class for product images (Webshop Settings →
// custom_product_image_ratio). "Original" (or unknown) → no class,
// theme default (fixed heights, object-fit: cover) applies.
webshop.get_image_ratio_class = function (settings) {
	const ratio = settings && settings.custom_product_image_ratio;
	// "Original" → keep theme defaults (fixed heights). Empty/unset → default
	// to 16:9 (PIT-TASK-2027-000135): grid cards are sized for 16:9 images.
	if (ratio === "Original") return "";
	if (!ratio) return " ws-img-ratio-16-9";
	return " ws-img-ratio-" + ratio.replace(":", "-");
};

// "New" badge (PIT-TASK-2027-000144). Rendered when the backend
// flagged the item (is_new) and a period is configured at all —
// the backend alone decides, the frontend just renders.
webshop.get_new_badge_html = function (item) {
	if (!item || !item.is_new) return "";
	return `<span class="ws-new-badge">${ __("New") }</span>`;
};

// Discount badge (PIT-TASK-2027-000142). Shows e.g. "50 %" as a red
// badge overlay on the product image when a general (non-customer,
// non-qty) pricing rule grants a discount percentage on the item.
// The backend (webshop_discount_badge.py) decides whether a badge
// applies and provides the text; the frontend just renders it.
webshop.get_discount_badge_html = function (item) {
	if (!item || !item.discount_badge) return "";
	return `<span class="ws-discount-badge">${ item.discount_badge }</span>`;
};

// ============================================================
// Portrait image size proportion (PIT-TASK-2027-000278)
// ============================================================
// Webshop Settings.custom_portrait_image_zoom (Percent 0-100) is
// injected as body classes `ws-img-portrait-zoom ws-img-portrait-zoom-<n>`
// by shopping_cart/utils.py (only when show_full_product_images is on —
// the contain mode where portrait photos render smaller than landscape).
// This helper zooms PORTRAIT images toward the cropped cover fill in
// steps: 0% = full image visible (contain, current behavior), 100% =
// maximum crop (cover). Landscape (and square) images are never touched.
// The computed transform is an inline scale(); the overview containers
// (.card-img-container / .list-image / .ws-single-img-link) all have
// overflow: hidden, so the scaled image is clipped to the box.

webshop.get_portrait_zoom = function () {
	const m = (document.body.className || "").match(/ws-img-portrait-zoom-(\d{1,3})/);
	if (!m) return 0;
	const z = parseInt(m[1], 10);
	return isNaN(z) || z < 0 ? 0 : Math.min(100, z);
};

webshop._apply_portrait_zoom_to_image = function (img, zoom) {
	if (img.dataset.wsPortraitZoomOk) return; // already handled (or skipped)
	const iw = img.naturalWidth, ih = img.naturalHeight;
	if (!iw || !ih) return; // not loaded yet / no natural size → retry later
	if (iw >= ih) {
		img.dataset.wsPortraitZoomOk = "1"; // landscape/square: never changed
		return;
	}
	const boxW = img.clientWidth, boxH = img.clientHeight; // layout box
	if (!boxW || !boxH) return; // not laid out yet → retry after the next frame
	// Scale between contain (whole image visible) and cover (fill+crop):
	// s_contain keeps the whole height, s_cover fills the width; the target
	// scale is a linear interpolation controlled by the zoom percent.
	const s_contain = Math.min(boxW / iw, boxH / ih);
	const s_cover = Math.max(boxW / iw, boxH / ih);
	const target = s_contain + (s_cover - s_contain) * (zoom / 100);
	const k = target / s_contain; // relative to the current contain render
	img.dataset.wsPortraitZoomOk = "1";
	if (Math.abs(k - 1) < 0.001) return; // zoom ≈ 0 → nothing to do
	img.style.transformOrigin = "center center";
	img.style.transform = `scale(${ k.toFixed(4) })`;
};

webshop.apply_portrait_image_zoom = function (rootEl) {
	const zoom = webshop.get_portrait_zoom();
	if (!zoom) return;
	const root = rootEl && rootEl.jquery ? rootEl[0] : (rootEl || document);
	if (!root || !root.querySelectorAll) return;
	const imgs = Array.from(root.querySelectorAll(
		".item-card-group-section .card-img-container img.card-img, " +
		".list-image img.card-img, .ws-single-img"
	));
	if (!imgs.length) return;

	const tryAll = () => {
		imgs.forEach((img) => webshop._apply_portrait_zoom_to_image(img, zoom));
	};

	// The card HTML is inserted first; layout (clientWidth/Height) and lazy
	// image loads only happen after this function returns. Retry once the
	// browser has laid the images out.
	tryAll();
	requestAnimationFrame(() => requestAnimationFrame(tryAll));
	imgs.forEach((img) => {
		if (!img.complete) {
			img.addEventListener("load", () => {
				tryAll();
				requestAnimationFrame(tryAll);
			}, { once: true });
		}
	});
};
